/**
 * Comprehensive Tailwind Color Mapper
 * Maps hex colors to Tailwind class names
 */

export const TAILWIND_COLORS = {
  // Slate
  '#f8fafc': 'slate-50', '#f1f5f9': 'slate-100', '#e2e8f0': 'slate-200',
  '#cbd5e1': 'slate-300', '#94a3b8': 'slate-400', '#64748b': 'slate-500',
  '#475569': 'slate-600', '#334155': 'slate-700', '#1e293b': 'slate-800',
  '#0f172a': 'slate-900',
  
  // Gray
  '#f9fafb': 'gray-50', '#f3f4f6': 'gray-100', '#e5e7eb': 'gray-200',
  '#d1d5db': 'gray-300', '#9ca3af': 'gray-400', '#6b7280': 'gray-500',
  '#4b5563': 'gray-600', '#374151': 'gray-700', '#1f2937': 'gray-800',
  '#111827': 'gray-900',
  
  // Orange
  '#fff7ed': 'orange-50', '#ffedd5': 'orange-100', '#fed7aa': 'orange-200',
  '#fdba74': 'orange-300', '#fb923c': 'orange-400', '#f97316': 'orange-500',
  '#ea580c': 'orange-600', '#c2410c': 'orange-700', '#9a3412': 'orange-800',
  '#7c2d12': 'orange-900',
  
  // Blue
  '#eff6ff': 'blue-50', '#dbeafe': 'blue-100', '#bfdbfe': 'blue-200',
  '#93c5fd': 'blue-300', '#60a5fa': 'blue-400', '#3b82f6': 'blue-500',
  '#2563eb': 'blue-600', '#1d4ed8': 'blue-700', '#1e40af': 'blue-800',
  '#1e3a8a': 'blue-900',
  
  // Green
  '#f0fdf4': 'green-50', '#dcfce7': 'green-100', '#bbf7d0': 'green-200',
  '#86efac': 'green-300', '#4ade80': 'green-400', '#22c55e': 'green-500',
  '#16a34a': 'green-600', '#15803d': 'green-700', '#166534': 'green-800',
  '#14532d': 'green-900',
  
  // Red
  '#fef2f2': 'red-50', '#fee2e2': 'red-100', '#fecaca': 'red-200',
  '#fca5a5': 'red-300', '#f87171': 'red-400', '#ef4444': 'red-500',
  '#dc2626': 'red-600', '#b91c1c': 'red-700', '#991b1b': 'red-800',
  '#7f1d1d': 'red-900',
  
  // Teal
  '#f0fdfa': 'teal-50', '#ccfbf1': 'teal-100', '#99f6e4': 'teal-200',
  '#5eead4': 'teal-300', '#2dd4bf': 'teal-400', '#14b8a6': 'teal-500',
  '#0d9488': 'teal-600', '#0f766e': 'teal-700', '#115e59': 'teal-800',
  '#134e4a': 'teal-900',
  
  // Purple
  '#faf5ff': 'purple-50', '#f3e8ff': 'purple-100', '#e9d5ff': 'purple-200',
  '#d8b4fe': 'purple-300', '#c084fc': 'purple-400', '#a855f7': 'purple-500',
  '#9333ea': 'purple-600', '#7e22ce': 'purple-700', '#6b21a8': 'purple-800',
  '#581c87': 'purple-900',
  
  // Pink
  '#fdf2f8': 'pink-50', '#fce7f3': 'pink-100', '#fbcfe8': 'pink-200',
  '#f9a8d4': 'pink-300', '#f472b6': 'pink-400', '#ec4899': 'pink-500',
  '#db2777': 'pink-600', '#be185d': 'pink-700', '#9d174d': 'pink-800',
  '#831843': 'pink-900',
  
  // Yellow
  '#fefce8': 'yellow-50', '#fef9c3': 'yellow-100', '#fef08a': 'yellow-200',
  '#fde047': 'yellow-300', '#facc15': 'yellow-400', '#eab308': 'yellow-500',
  '#ca8a04': 'yellow-600', '#a16207': 'yellow-700', '#854d0e': 'yellow-800',
  '#713f12': 'yellow-900',
  
  // Indigo
  '#eef2ff': 'indigo-50', '#e0e7ff': 'indigo-100', '#c7d2fe': 'indigo-200',
  '#a5b4fc': 'indigo-300', '#818cf8': 'indigo-400', '#6366f1': 'indigo-500',
  '#4f46e5': 'indigo-600', '#4338ca': 'indigo-700', '#3730a3': 'indigo-800',
  '#312e81': 'indigo-900',
  
  // Emerald
  '#ecfdf5': 'emerald-50', '#d1fae5': 'emerald-100', '#a7f3d0': 'emerald-200',
  '#6ee7b7': 'emerald-300', '#34d399': 'emerald-400', '#10b981': 'emerald-500',
  '#059669': 'emerald-600', '#047857': 'emerald-700', '#065f46': 'emerald-800',
  '#064e3b': 'emerald-900',
  
  // White/Black
  '#ffffff': 'white',
  '#fff': 'white',
  '#000000': 'black',
  '#000': 'black',
};

/**
 * Convert hex color to Tailwind class name
 * @param {string} hex - Hex color (e.g., "#ff0000")
 * @param {string} prefix - Tailwind prefix ("text", "bg", "border")
 * @returns {string} Tailwind class (e.g., "text-red-500")
 */
export function hexToTailwind(hex, prefix = 'text') {
  if (!hex) return `${prefix}-gray-500`;
  
  const normalized = hex.toLowerCase().trim();
  const colorName = TAILWIND_COLORS[normalized];
  
  if (colorName) {
    return `${prefix}-${colorName}`;
  }
  
  // If not found, try to find closest color
  const closest = findClosestColor(normalized);
  if (closest) {
    return `${prefix}-${closest}`;
  }
  
  // Ultimate fallback
  return `${prefix}-gray-500`;
}

/**
 * Convert Tailwind class to hex color
 * @param {string} tailwindClass - Tailwind class (e.g., "text-red-500")
 * @returns {string} Hex color (e.g., "#ef4444")
 */
export function tailwindToHex(tailwindClass) {
  if (!tailwindClass) return '#6b7280';
  
  // Extract color name from class (remove prefix)
  const match = tailwindClass.match(/(?:text|bg|border)-(.+)/);
  if (!match) return '#6b7280';
  
  const colorName = match[1];
  
  // Find in reverse map
  for (const [hex, name] of Object.entries(TAILWIND_COLORS)) {
    if (name === colorName) {
      return hex;
    }
  }
  
  return '#6b7280'; // gray-500 fallback
}

/**
 * Find closest Tailwind color to given hex
 * @param {string} hex - Hex color
 * @returns {string|null} Closest Tailwind color name
 */
function findClosestColor(hex) {
  // Convert hex to RGB
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  
  let closestColor = null;
  let minDistance = Infinity;
  
  for (const [colorHex, colorName] of Object.entries(TAILWIND_COLORS)) {
    const colorRgb = hexToRgb(colorHex);
    if (!colorRgb) continue;
    
    const distance = colorDistance(rgb, colorRgb);
    if (distance < minDistance) {
      minDistance = distance;
      closestColor = colorName;
    }
  }
  
  return closestColor;
}

/**
 * Convert hex to RGB
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Calculate color distance (Euclidean)
 */
function colorDistance(rgb1, rgb2) {
  return Math.sqrt(
    Math.pow(rgb1.r - rgb2.r, 2) +
    Math.pow(rgb1.g - rgb2.g, 2) +
    Math.pow(rgb1.b - rgb2.b, 2)
  );
}

/**
 * Get all available Tailwind colors grouped by family
 */
export function getColorFamilies() {
  const families = {};
  
  for (const [hex, name] of Object.entries(TAILWIND_COLORS)) {
    const [family] = name.split('-');
    if (!families[family]) {
      families[family] = [];
    }
    families[family].push({ hex, name });
  }
  
  return families;
}

